// components/TemplateManagement.jsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Modal,
  Form,
  Alert,
  Spinner,
  Badge,
  ButtonGroup,
  Tabs,
  Tab
} from 'react-bootstrap';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaCopy,
  FaSave,
  FaTimes,
  FaCrown,
  FaStar,
  FaPalette,
  FaFont,
  FaFileAlt
} from 'react-icons/fa';
import {
  getAvailableTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  cloneTemplate,
  getTemplateById
} from '../services/templateService';
import PipsqueakReportBuilder from './ReportBuilder';
import './TemplateManagement.css';

function TemplateManagement() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Modal states
  const [showEditBuilder, setShowEditBuilder] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);

  // Form states
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Clone form
  const [cloneName, setCloneName] = useState('');

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const fetchedTemplates = await getAvailableTemplates();
      setTemplates(fetchedTemplates);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };


  const handleSaveEditedTemplate = async (templateData) => {
    try {
      setError(null);
      
      const updateData = {
        templateName: templateData.name || selectedTemplate.name,
        templateType: 'pdf',
        configuration: templateData.configuration
      };
      
      const updatedTemplate = await updateTemplate(selectedTemplate.id, updateData);
      
      setSuccess(`Template "${updatedTemplate.name || selectedTemplate.name}" updated successfully!`);
      setShowEditBuilder(false);
      setSelectedTemplate(null);
      loadTemplates();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async () => {
    try {
      setError(null);
      
      await deleteTemplate(selectedTemplate.id);
      
      setSuccess(`Template "${selectedTemplate.name}" deleted successfully!`);
      setShowDeleteModal(false);
      setSelectedTemplate(null);
      loadTemplates();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleClone = async () => {
    try {
      setError(null);
      
      const clonedTemplate = await cloneTemplate(selectedTemplate.id, cloneName);
      
      setSuccess(`Template "${clonedTemplate.name}" cloned successfully!`);
      setShowCloneModal(false);
      setCloneName('');
      setSelectedTemplate(null);
      loadTemplates();
    } catch (err) {
      setError(err.message);
    }
  };

  const openCreateBuilder = () => {
    // Set up a blank template for creation
    setSelectedTemplate({
      id: null,
      name: 'New Template',
      configuration: {
        pages: [{ id: 'page-1', name: 'Page 1', blocks: [] }],
        theme: {
          primaryColor: '#3b82f6',
          secondaryColor: '#1e40af',
          logo: null,
          header: 'Pipsqueak Security Report',
          footer: 'Confidential - Generated by Pipsqueak',
          companyName: '',
          watermark: false
        }
      }
    });
    setShowEditBuilder(true);
  };

  const openEditBuilder = async (template) => {
    try {
      setError(null);
      // Fetch full template data including configuration
      const fullTemplate = await getTemplateById(template.id);
      setSelectedTemplate(fullTemplate);
      setShowEditBuilder(true);
    } catch (err) {
      setError(`Failed to load template: ${err.message}`);
    }
  };

  const openDeleteModal = (template) => {
    setSelectedTemplate(template);
    setShowDeleteModal(true);
  };

  const openCloneModal = (template) => {
    setSelectedTemplate(template);
    setCloneName(`${template.name} (Copy)`);
    setShowCloneModal(true);
  };


  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading templates...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="template-management py-4">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2>Template Management</h2>
              <p className="text-muted">Manage your custom report templates</p>
            </div>
            <Button variant="primary" onClick={openCreateBuilder}>
              <FaPlus className="me-2" />
              Create New Template
            </Button>
          </div>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card>
        <Card.Body>
          <Table responsive hover>
            <thead>
              <tr>
                <th>Template Name</th>
                <th>Type</th>
                <th>Base Template</th>
                <th>Description</th>
                <th>Created</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((template) => (
                <tr key={template.id}>
                  <td>
                    <div className="d-flex align-items-center">
                      {template.is_default ? (
                        <FaCrown className="text-warning me-2" />
                      ) : (
                        <FaStar className="text-primary me-2" />
                      )}
                      <strong>{template.name}</strong>
                    </div>
                  </td>
                  <td>
                    <Badge bg={template.is_default ? 'warning' : 'info'}>
                      {template.is_default ? 'Default' : 'Custom'}
                    </Badge>
                  </td>
                  <td>
                    <Badge bg="secondary">{template.base_template}</Badge>
                  </td>
                  <td className="text-muted small">
                    {template.description || 'No description'}
                  </td>
                  <td className="text-muted small">
                    {new Date(template.created_at).toLocaleDateString()}
                  </td>
                  <td className="text-end">
                    <ButtonGroup size="sm">
                      <Button
                        variant="outline-primary"
                        onClick={() => openCloneModal(template)}
                        title="Clone Template"
                      >
                        <FaCopy />
                      </Button>
                      {!template.is_default && (
                        <>
                          <Button
                            variant="outline-warning"
                            onClick={() => openEditBuilder(template)}
                            title="Edit Template (Drag & Drop)"
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            variant="outline-danger"
                            onClick={() => openDeleteModal(template)}
                            title="Delete Template"
                          >
                            <FaTrash />
                          </Button>
                        </>
                      )}
                    </ButtonGroup>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {templates.length === 0 && (
            <div className="text-center py-5 text-muted">
              <p>No templates found. Create your first template to get started!</p>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Report Builder Modal for Create/Edit */}
      <Modal
        show={showEditBuilder}
        onHide={() => {
          setShowEditBuilder(false);
          setSelectedTemplate(null);
        }}
        fullscreen
        dialogClassName="template-builder-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaEdit className="me-2" />
            {selectedTemplate?.id ? `Edit Template: ${selectedTemplate.name}` : 'Create New Template'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0" style={{ height: 'calc(100vh - 60px)' }}>
          {selectedTemplate && (
            <PipsqueakReportBuilder
              scanData={{}} // Empty scan data for template design
              initialTemplate={{
                name: selectedTemplate.name,
                pages: selectedTemplate.configuration?.pages || [{ id: 'page-1', name: 'Page 1', blocks: [] }],
                theme: selectedTemplate.configuration?.theme || {
                  primaryColor: '#3b82f6',
                  secondaryColor: '#1e40af',
                  logo: null,
                  header: 'Pipsqueak Security Report',
                  footer: 'Confidential - Generated by Pipsqueak',
                  companyName: '',
                  watermark: false
                }
              }}
              onSaveTemplate={async (templateData) => {
                if (selectedTemplate?.id) {
                  // Update existing template
                  await handleSaveEditedTemplate({
                    name: templateData.name,
                    configuration: {
                      pages: templateData.pages,
                      theme: templateData.theme
                    }
                  });
                } else {
                  // Create new template
                  try {
                    setError(null);
                    const newTemplate = await createTemplate({
                      templateName: templateData.name,
                      templateType: 'pdf',
                      isDefault: false,
                      configuration: {
                        pages: templateData.pages,
                        theme: templateData.theme
                      }
                    });
                    setSuccess(`Template "${newTemplate.name}" created successfully!`);
                    setShowEditBuilder(false);
                    setSelectedTemplate(null);
                    loadTemplates();
                  } catch (err) {
                    setError(err.message);
                  }
                }
              }}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <FaTrash className="me-2" />
            Are you sure you want to delete the template "<strong>{selectedTemplate?.name}</strong>"?
            This action cannot be undone.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            <FaTrash className="me-2" />
            Delete Template
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Clone Template Modal */}
      <Modal show={showCloneModal} onHide={() => setShowCloneModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Clone Template</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>New Template Name</Form.Label>
            <Form.Control
              type="text"
              value={cloneName}
              onChange={(e) => setCloneName(e.target.value)}
              placeholder="Enter name for cloned template"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCloneModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleClone} disabled={!cloneName.trim()}>
            <FaCopy className="me-2" />
            Clone Template
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}


export default TemplateManagement;

